import React from "react";
import clsx from "clsx";

import type {
    GridProps,
    GetRowIdFunc,
    ColDef,
    GridReadyEvent,
    IHeaderParams,
    ICellRendererParams,
} from "@/ui/molecules/Grid";
import Grid, { GridLoaderSkeleton } from "@/ui/molecules/Grid";
import GridHeaderComponent from "@/ui/molecules/GridHeaderComponent";

import Box from "@/ui/atoms/Box";
import Typography from "@/ui/atoms/Typography";
import Badge from "@/ui/atoms/Badge";
import GridMappingsDropdownItemAccounts from "../GridMappings/GridMappingsDropdownItemAccounts";
import type { TOptionWithType } from "../typesGrids";

export const NOT_MAPPED_ACCOUNT_VALUE = "~~NOT_MAPPED_ACCOUNT_VALUE~~";

type TAlteredCustomAccount = {
    accountCode: string;
    mappedAccountCode: {
        from: string | null;
        to: string | null;
    };
};

const convertNullMappedAccountsToUnmappedAccountValueInRowData = (
    rowData: TChartOfAccountsRow[],
): (Omit<TChartOfAccountsRow, "mappedAccountCode"> & {
    mappedAccountCode: string | null;
})[] => {
    return rowData.map(row => ({
        ...row,
        mappedAccountCode:
            row.mappedAccountCode === null
                ? NOT_MAPPED_ACCOUNT_VALUE
                : row.mappedAccountCode,
        originalMappedAccountCode:
            row.originalMappedAccountCode === null
                ? NOT_MAPPED_ACCOUNT_VALUE
                : row.originalMappedAccountCode,
    }));
};

export type TSubcategoryInfo = {
    name: string;
    isNonOperating: boolean;
};

export type TChartOfAccountsRow = {
    type: "income" | "expense";
    code: string;
    account: string;
    subcategories: TSubcategoryInfo[];
    mappedAccountCode: string | null;
    mappedSection: string | null;
    mappedSubcategory: string | null;
    originalMappedAccountCode: string | null;
};

export type GridChartOfAccountsMappingProps = Omit<
    GridProps<TChartOfAccountsRow>,
    "columnDefs" | "rowData" | "getRowStyle"
> & {
    rowData: TChartOfAccountsRow[];
    optionsAccounts: TOptionWithType[];
    alteredAccounts: TAlteredCustomAccount[];
    onGridReady?: (params: GridReadyEvent) => void;
    onChangeAccountMapping: (params: {
        accountCode: string;
        mappedAccountCode: {
            from: string | null;
            to: string | null;
        };
    }) => void;
};

export const testIds = {
    main: "GridChartOfAccountsMapping",
    header: "GridChartOfAccountsMapping-header",
    badge: "GridChartOfAccountsMapping-badge",
    titleBar: "GridChartOfAccountsMapping-titleBar",
};

const CUSTOM_MAPPINGS_COLUMN_COUNT = 3;

const CellRenderer = (params: { value: string }) => {
    const value = params.value;

    return <Typography variant="body2">{value}</Typography>;
};

const SubcategoriesCellRenderer = (params: { value: TSubcategoryInfo[] }) => {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                height: "100%",
                flexWrap: "wrap",
            }}
        >
            {params.value.map((subcategory, index) => (
                <React.Fragment key={subcategory.name}>
                    <Typography variant="body2" component="span">
                        {subcategory.name}
                        {!subcategory.isNonOperating &&
                            index < params.value.length - 1 &&
                            ","}
                    </Typography>
                    {subcategory.isNonOperating && (
                        <Badge severity="inactive">
                            <Typography
                                variant="body4"
                                sx={{ textTransform: "capitalize" }}
                            >
                                non-operating
                            </Typography>
                        </Badge>
                    )}
                    {subcategory.isNonOperating &&
                        index < params.value.length - 1 && (
                            <Typography variant="body2" component="span">
                                ,
                            </Typography>
                        )}
                </React.Fragment>
            ))}
        </Box>
    );
};

const HeaderComponent = (params: IHeaderParams) => (
    <GridHeaderComponent {...params} textAlign="left" />
);

const GridChartOfAccountsMapping = React.memo(
    (props: GridChartOfAccountsMappingProps) => {
        const {
            rowData: rowDataFromProps,
            optionsAccounts,
            alteredAccounts,
            className,
            onGridReady,
            onChangeAccountMapping,
            ...otherProps
        } = props;

        const alteredAccountCodes = React.useMemo(
            () => alteredAccounts.map(account => account.accountCode),
            [alteredAccounts],
        );

        const rowData = React.useMemo(
            () =>
                convertNullMappedAccountsToUnmappedAccountValueInRowData(
                    rowDataFromProps,
                ),
            [rowDataFromProps],
        );

        const optionsAccountsWithUnmappedOption = React.useMemo(() => {
            return [
                {
                    label: "Unmapped",
                    value: NOT_MAPPED_ACCOUNT_VALUE,
                },
                ...optionsAccounts,
            ];
        }, [optionsAccounts]);

        const accountsValues = React.useMemo(
            () => optionsAccounts.map(option => option.value),
            [optionsAccounts],
        );
        const accountsValuesWithUnmappedOption = React.useMemo(
            () => optionsAccountsWithUnmappedOption.map(option => option.value),
            [optionsAccountsWithUnmappedOption],
        );
        const accountsReference = React.useMemo(
            () =>
                optionsAccountsWithUnmappedOption.reduce(
                    (acc, option) => ({
                        ...acc,
                        [option.value]: option.label,
                    }),
                    {} as Record<string, string>,
                ),
            [optionsAccountsWithUnmappedOption],
        );

        const getRowId: GetRowIdFunc<TChartOfAccountsRow> = React.useCallback(
            params => String(params.data.code),
            [],
        );

        const CellRendererAccount = React.useCallback(
            (
                params: ICellRendererParams<TChartOfAccountsRow, string | null>,
            ) => (
                <GridMappingsDropdownItemAccounts
                    optionsAccounts={optionsAccountsWithUnmappedOption}
                    displayRightIcon={false}
                    isRowOmitted={false}
                    {...params}
                />
            ),
            [optionsAccountsWithUnmappedOption],
        );

        const CellRendererAccountDropdown = React.useCallback(
            (
                params: ICellRendererParams<TChartOfAccountsRow, string | null>,
            ) => (
                <GridMappingsDropdownItemAccounts
                    optionsAccounts={optionsAccountsWithUnmappedOption}
                    displayRightIcon={true}
                    isRowOmitted={false}
                    isAltered={(() => {
                        const wasAlteredByUser =
                            params.data?.code !== undefined
                                ? alteredAccountCodes.includes(params.data.code)
                                : false;
                        const wasLoadedAlteredFromServer =
                            params.data?.mappedAccountCode !==
                            params.data?.originalMappedAccountCode;

                        return wasAlteredByUser || wasLoadedAlteredFromServer;
                    })()}
                    isReverted={(() => {
                        const wasAlteredByUser =
                            params.data?.code !== undefined
                                ? alteredAccountCodes.includes(params.data.code)
                                : false;

                        const alteredAccount = alteredAccounts.find(
                            account =>
                                account.accountCode === params.data?.code,
                        );

                        const userChangeIsTheSameAsTheOriginal =
                            wasAlteredByUser &&
                            alteredAccount?.mappedAccountCode.to === null
                                ? NOT_MAPPED_ACCOUNT_VALUE ===
                                  params.data?.originalMappedAccountCode
                                : alteredAccount?.mappedAccountCode.to ===
                                  params.data?.originalMappedAccountCode;

                        return userChangeIsTheSameAsTheOriginal;
                    })()}
                    {...params}
                />
            ),
            [
                optionsAccountsWithUnmappedOption,
                alteredAccountCodes,
                alteredAccounts,
            ],
        );

        const TypeCellRenderer = React.useCallback(
            (params: { value: string }) => {
                const value = params.value as "income" | "expense";

                const severity = value === "income" ? "success" : "error";

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                            paddingLeft: "16px",
                        }}
                    >
                        <Badge data-testid={testIds.badge} severity={severity}>
                            <Typography
                                variant="body4"
                                sx={{
                                    textTransform: "capitalize",
                                }}
                            >
                                {value}
                            </Typography>
                        </Badge>
                    </Box>
                );
            },
            [],
        );

        const columnDefAccount = React.useMemo(
            () => ({
                headerName: "Mapped Account Name",
                headerComponent: GridHeaderComponent,
                field: "mappedAccountCode",
                editable: true,
                cellEditor: "agRichSelectCellEditor",
                width: 348,
                minWidth: 348,
                colSpan: (params: { data?: TChartOfAccountsRow }) =>
                    params.data?.mappedAccountCode === NOT_MAPPED_ACCOUNT_VALUE
                        ? CUSTOM_MAPPINGS_COLUMN_COUNT // span all columns when unmapped — SubCat and Section/Total would be empty
                        : 1,
                cellEditorParams: (params: { data?: TChartOfAccountsRow }) => {
                    const hasNonOperatingSubcategory =
                        params.data?.subcategories.some(
                            sub => sub.isNonOperating,
                        ) ?? false;
                    const displayUnmappedOption = hasNonOperatingSubcategory;

                    return {
                        allowTyping: true,
                        filterList: true,
                        valueListMaxHeight: 220,
                        searchType: "matchAny",
                        cellHeight: 48,
                        cellRenderer: CellRendererAccount,
                        values: displayUnmappedOption
                            ? accountsValuesWithUnmappedOption
                            : accountsValues,
                    };
                },
                refData: { ...accountsReference },
                cellRenderer: CellRendererAccountDropdown,
                suppressMovable: true,
                cellStyle: {
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    display: "block",
                    paddingRight: "0px",
                    paddingLeft: "0px",
                },
            }),
            [
                accountsValues,
                accountsValuesWithUnmappedOption,
                accountsReference,
                CellRendererAccount,
                CellRendererAccountDropdown,
            ],
        );

        const columnDefs = React.useMemo<ColDef<TChartOfAccountsRow>[]>(
            () => [
                {
                    headerName: "PRODA Chart of Accounts",
                    children: [
                        {
                            field: "type",
                            headerName: "Type",
                            headerComponent: HeaderComponent,
                            width: 150,
                            cellRenderer: TypeCellRenderer,
                            sortable: true,
                        },
                        {
                            field: "account",
                            headerName: "Account",
                            headerComponent: HeaderComponent,
                            flex: 1,
                            minWidth: 200,
                            cellRenderer: CellRenderer,
                        },
                        {
                            field: "subcategories",
                            headerName: "Sub-Categories",
                            headerComponent: HeaderComponent,
                            flex: 1,
                            minWidth: 200,
                            cellRenderer: SubcategoriesCellRenderer,
                            valueFormatter: (params: {
                                value?: TSubcategoryInfo[];
                            }) =>
                                params.value?.map(sub => sub.name).join(", ") ??
                                "",
                        },
                    ],
                },
                {
                    headerName: "Custom mappings",
                    children: [
                        columnDefAccount,
                        {
                            field: "mappedSubcategory",
                            headerName: "Sub-Category",
                            headerComponent: HeaderComponent,
                            flex: 1,
                            minWidth: 200,
                            cellRenderer: CellRenderer,
                        },
                        {
                            field: "mappedSection",
                            headerName: "Section / Total",
                            headerComponent: HeaderComponent,
                            flex: 1,
                            minWidth: 200,
                            cellRenderer: CellRenderer,
                        },
                    ],
                },
            ],
            [TypeCellRenderer, columnDefAccount],
        );

        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    width: "100%",
                }}
            >
                <Grid<TChartOfAccountsRow>
                    data-testid={testIds.main}
                    className={clsx([testIds.main, className])}
                    gridTheme="ag-theme-custom-proda"
                    readOnlyEdit={true}
                    onCellEditRequest={event => {
                        const field = event.colDef.field;
                        if (field !== "mappedAccountCode") return;

                        const oldValue =
                            event.oldValue === NOT_MAPPED_ACCOUNT_VALUE
                                ? null
                                : (event.oldValue as string);
                        const newValue =
                            event.newValue === NOT_MAPPED_ACCOUNT_VALUE
                                ? null
                                : (event.newValue as string);

                        const bothValuesAreTheSame = oldValue === newValue;
                        if (bothValuesAreTheSame) return;

                        const accountCode = event.data.code;

                        onChangeAccountMapping?.({
                            accountCode,
                            mappedAccountCode: {
                                from: oldValue,
                                to: newValue,
                            },
                        });
                    }}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    getRowId={getRowId}
                    defaultColDef={{
                        suppressMovable: true,
                        sortable: false,
                        resizable: true,
                    }}
                    getRowStyle={() => ({
                        backgroundColor: "var(--proda-white)",
                    })}
                    suppressHeaderFocus={true}
                    enableBrowserTooltips={true}
                    onGridReady={onGridReady}
                    headerHeight={48}
                    groupHeaderHeight={48}
                    gridLoaderComponent={GridLoaderSkeleton}
                    suppressRowVirtualisation={true}
                    suppressColumnVirtualisation={true}
                    {...otherProps}
                />
            </Box>
        );
    },
);

GridChartOfAccountsMapping.displayName = "GridChartOfAccountsMapping";

export default GridChartOfAccountsMapping;
